import { PLAYBOOKS, type PlaybookId } from "./playbooks";

export type PlaybookStatus = "in_progress" | "completed" | "dismissed";

export interface PlaybookProgress {
  status: PlaybookStatus;
  step?: number;
}

export type PlaybookStore = Partial<Record<PlaybookId, PlaybookProgress>>;

function key(userId: string): string {
  return `salesapp:playbooks:${userId}`;
}

export function loadPlaybookStore(userId: string): PlaybookStore {
  try {
    const raw = window.localStorage.getItem(key(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PlaybookStore;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function savePlaybookStore(userId: string, store: PlaybookStore): void {
  window.localStorage.setItem(key(userId), JSON.stringify(store));
}

const TOUR_IDS: PlaybookId[] = PLAYBOOKS.map((item) => item.id);

function cleanProgress(row: PlaybookProgress | undefined): PlaybookProgress | undefined {
  if (!row || typeof row !== "object") return undefined;
  if (row.status !== "in_progress" && row.status !== "completed" && row.status !== "dismissed") return undefined;
  const step = typeof row.step === "number" && row.step >= 0 && row.step <= 40 ? row.step : undefined;
  return { status: row.status, step };
}

/** A finished tour stays finished. Replaying it must not write in-progress or dismissed over the star. */
export function applyProgress(
  store: PlaybookStore,
  id: PlaybookId,
  status: PlaybookStatus,
  step?: number,
): PlaybookStore {
  const current = cleanProgress(store[id]);
  if (current?.status === "completed" && status !== "completed") return store;
  const nextStep = typeof step === "number" ? step : undefined;
  if (current?.status === status && current.step === nextStep) return store;
  return { ...store, [id]: { status, step: nextStep } };
}

/**
 * Server progress wins for an unfinished tour (another browser exited or moved on).
 * Either side being completed keeps the star, so a new device still shows it and a
 * replay on this one cannot clear it.
 */
export function mergePlaybookStores(local: PlaybookStore, remote: PlaybookStore | null): PlaybookStore {
  if (!remote) return local;
  const merged: PlaybookStore = {};
  for (const id of TOUR_IDS) {
    const left = cleanProgress(local[id]);
    const right = cleanProgress(remote[id]);
    if (left?.status === "completed" || right?.status === "completed") {
      const kept = left?.status === "completed" ? left : right;
      merged[id] = { status: "completed", step: kept?.step };
    } else if (right) merged[id] = right;
    else if (left) merged[id] = left;
  }
  return merged;
}

export function toursDiffer(left: PlaybookStore, right: PlaybookStore): boolean {
  const snap = (store: PlaybookStore) => TOUR_IDS.map((id) => cleanProgress(store[id]) ?? null);
  return JSON.stringify(snap(left)) !== JSON.stringify(snap(right));
}

export function completedTourIds(store: PlaybookStore, ids: PlaybookId[]): PlaybookId[] {
  return ids.filter((id) => store[id]?.status === "completed");
}

export function shouldAutoStart(store: PlaybookStore, id: PlaybookId): boolean {
  const row = store[id];
  return !row || (row.status !== "completed" && row.status !== "dismissed");
}
